import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';

/**
 * About / how-to-play page. Explains the daily format, the rules drawn from the
 * engine (3 lives, target score, +50 bingo, premium squares), and a light
 * overview of the word list. Styling mirrors the Privacy/Terms prose pages.
 */
export default function About() {
  return (
    <section data-testid="page-about" className="prose-stone mx-auto max-w-prose">
      <Seo
        title="How to Play — Scrabble Daily, the Free Daily Word Puzzle"
        description={
          'How to play Scrabble Daily: place tiles crossword-style and hit the target ' +
          'score within the move limit. Three boards, three lives, one bag — plus how ' +
          'the word list handles UK and US spellings.'
        }
        path="/about"
      />
      <h1 className="text-2xl font-bold text-stone-900">About Scrabble Daily</h1>

      <p className="mt-4 text-stone-700">
        Scrabble Daily is a free daily word game. Every day there are three fresh puzzles — Easy,
        Medium, and Hard — and everyone plays the exact same boards, so the leaderboard is a fair
        race. Three boards, three lives, one bag.
      </p>

      <h2 className="mt-6 text-lg font-bold text-stone-900">How to play</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-stone-700">
        <li>
          Pick a difficulty. You start from a board that already has a few words on it, with a rack
          of seven tiles and a bag to draw from.
        </li>
        <li>
          Place tiles like classic Scrabble: in a single row or column, connected to what&apos;s
          already on the board, reading left-to-right or top-to-bottom. The first word must cross
          the centre star (★).
        </li>
        <li>
          Tiles score their usual Scrabble values. Premium squares boost a letter (
          <strong>DL</strong>, <strong>TL</strong>) or a whole word (<strong>DW</strong>,{' '}
          <strong>TW</strong>), and playing all seven tiles at once is a <strong>bingo</strong> for
          a <strong>+50</strong> bonus. Blank tiles are wildcards worth 0 points.
        </li>
        <li>
          Reach the <strong>target score</strong> within the move limit to win the board.
        </li>
        <li>
          You get <strong>3 lives</strong> per board each day — resetting the board spends a life.
          Win once and the day is done, though you can keep playing any remaining lives to push for
          a higher score.
        </li>
        <li>
          Winning builds a day streak, and your scores feed the{' '}
          <Link to="/leaderboard" className="font-semibold text-emerald-700 underline">
            leaderboard
          </Link>
          .
        </li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-stone-900">The word list</h2>
      <p className="mt-2 text-stone-700">
        We use a large, open English word list rather than an official tournament dictionary. It is
        based on American English but accepts British spellings too — so both{' '}
        <strong>RUMORS</strong> and <strong>RUMOURS</strong>, <strong>COLOR</strong> and{' '}
        <strong>COLOUR</strong>, are valid plays. It doesn&apos;t include proper nouns or
        abbreviations.
      </p>

      <p className="mt-6 text-sm text-stone-500">
        Questions or a word you think we got wrong? See{' '}
        <Link to="/privacy" className="font-semibold text-emerald-700 underline">
          Privacy
        </Link>{' '}
        and{' '}
        <Link to="/terms" className="font-semibold text-emerald-700 underline">
          Terms
        </Link>
        .
      </p>
    </section>
  );
}
